import {defineStore, storeToRefs} from 'pinia';
import {computedAsync, until, useLocalStorage} from '@vueuse/core';
import type {
    BookingFloor,
    BookingLocation,
    BookingRoom,
    SpaceStructure,
} from '../../api/models/booking/spaceModels';
import {computed, shallowRef, watch} from 'vue';
import {useRoute} from 'vue-router';
import {getSpaceStructure} from '../../api/repositories/booking/SpaceRepository';
import {useBookingForStore} from '../bookingForStore';
import {useUserStore} from '../userStore';
import z from 'zod';
import {BookingViewType, type CurrentView} from '@/api/models/booking/bookingViewType';
import {useResourceTypeStore} from './resourceTypesStore';
import {useFeatureFlagStore} from '../featureFlagStore';

const selectedSpacesModel = z.object({
    locationId: z.string().nullable(),
    floorId: z.string().nullable(),
    roomId: z.string().nullable(),
});

type SelectedSpaces = z.infer<typeof selectedSpacesModel>;

export const useSpaceSelectionStore = defineStore('spaceSelectionStore', () => {
    const selectedSpaces = useLocalStorage<SelectedSpaces>(
        'booking:selectedSpaces',
        {
            locationId: null,
            floorId: null,
            roomId: null,
        },
        {
            serializer: {
                read: value => {
                    try {
                        return selectedSpacesModel.parse(JSON.parse(value));
                    } catch {
                        return {
                            locationId: null,
                            floorId: null,
                            roomId: null,
                        };
                    }
                },
                write: value => JSON.stringify(value),
            },
        }
    );

    const selectedLocationId = computed<string | null>(() => selectedSpaces.value.locationId);
    const selectedFloorId = computed<string | null>(() => selectedSpaces.value.floorId);
    const selectedRoomId = computed<string | null>(() => selectedSpaces.value.roomId);

    const {user} = storeToRefs(useUserStore());
    watch(user, (current, previous) => {
        if (!previous || current?.id === previous.id) {
            return;
        }
        // Reset selections when user changes
        selectedSpaces.value = {
            locationId: null,
            floorId: null,
            roomId: null,
        };
    });

    const {userMeBookingFor} = storeToRefs(useBookingForStore());

    const {resourceTypes} = storeToRefs(useResourceTypeStore());
    const {bookingSpaceAvailabilities} = storeToRefs(useFeatureFlagStore());

    function sortResourcesInStructure(structure: SpaceStructure): SpaceStructure {
        const sortResources = (resources: {resourceType: string; count: number}[]) => {
            return [...resources].sort((resourceA, resourceB) => {
                const resourceTypeA = resourceTypes.value.find(
                    resourceType => resourceType.id === resourceA.resourceType
                );
                const resourceTypeB = resourceTypes.value.find(
                    resourceType => resourceType.id === resourceB.resourceType
                );
                return (resourceTypeA?.priority ?? 0) - (resourceTypeB?.priority ?? 0);
            });
        };

        return {
            ...structure,
            counts: {
                ...structure.counts,
                resources: sortResources(structure.counts.resources),
            },
            locations: structure.locations.map(location => ({
                ...location,
                counts: {
                    ...location.counts,
                    resources: sortResources(location.counts.resources),
                },
                floors: location.floors.map(floor => ({
                    ...floor,
                    counts: {
                        ...floor.counts,
                        resources: sortResources(floor.counts.resources),
                    },
                    rooms: floor.rooms.map(room => ({
                        ...room,
                        counts: {
                            ...room.counts,
                            resources: sortResources(room.counts.resources),
                        },
                    })),
                })),
            })),
        };
    }

    const spaceStructureIsLoading = shallowRef(false);
    const spaceStructure = computedAsync<SpaceStructure | null>(
        async () => {
            if (!user.value?.id) {
                return null;
            }
            const structure = await getSpaceStructure(userMeBookingFor.value?.id);
            return bookingSpaceAvailabilities.value
                ? sortResourcesInStructure(structure)
                : structure;
        },
        null,
        spaceStructureIsLoading
    );

    const roomsWithParentData = computed(() => {
        const rooms =
            spaceStructure.value?.locations
                .map(location =>
                    location.floors.map(floor =>
                        floor.rooms.map(room => ({
                            ...room,
                            location,
                            floor,
                        }))
                    )
                )
                .flat(3) ?? [];

        return rooms;
    });

    const selectedLocation = computed<BookingLocation | null>(() => {
        return (
            spaceStructure.value?.locations?.find(
                location => location.id === selectedLocationId.value
            ) ?? null
        );
    });

    const selectedFloor = computed<BookingFloor | null>(() => {
        if (selectedFloorId.value === null) {
            return null;
        }

        return (
            selectedLocation.value?.floors.find(floor => floor.id === selectedFloorId.value) ?? null
        );
    });

    const selectedRoom = computed<BookingRoom | null>(() => {
        if (selectedRoomId.value === null) {
            return null;
        }

        return selectedFloor.value?.rooms.find(room => room.id === selectedRoomId.value) ?? null;
    });

    /**
     * Provides default selections when there's exactly one option at each level of the space hierarchy.
     * Properties will contain respective objects if exactly one exists, otherwise null
     */
    const singleSelectionDefaults = computed(() => ({
        location:
            spaceStructure.value?.locations.length === 1 ? spaceStructure.value.locations[0] : null,
        floor:
            selectedLocation.value?.floors.length === 1 ? selectedLocation.value.floors[0] : null,
        room: selectedFloor.value?.rooms.length === 1 ? selectedFloor.value.rooms[0] : null,
    }));

    function selectLocation(locationId: string) {
        selectedSpaces.value = {
            locationId,
            floorId: null,
            roomId: null,
        };
    }

    async function selectFloor(floorId: string) {
        await until(roomsWithParentData).toMatch(rooms => rooms.length > 0);
        const parentLocation = roomsWithParentData.value.find(
            room => room.floor.id === floorId
        )?.location;

        if (!parentLocation) {
            selectedSpaces.value = {
                ...selectedSpaces.value,
                floorId: null,
            };
            return;
        }

        selectedSpaces.value = {
            locationId: parentLocation.id,
            floorId,
            roomId: null,
        };
    }

    async function selectRoom(roomId: string) {
        await until(roomsWithParentData).toMatch(rooms => rooms.length > 0);
        const room = roomsWithParentData.value.find(room => room.id === roomId);

        if (!room) {
            selectedSpaces.value = {
                ...selectedSpaces.value,
                roomId: null,
            };
            return;
        }

        selectedSpaces.value = {
            locationId: room.location.id,
            floorId: room.floor.id,
            roomId,
        };
    }

    function deselectLocation() {
        selectedSpaces.value = {
            locationId: null,
            floorId: null,
            roomId: null,
        };
    }

    function deselectFloor() {
        selectedSpaces.value = {
            ...selectedSpaces.value,
            floorId: null,
            roomId: null,
        };
    }

    function deselectRoom() {
        selectedSpaces.value = {
            ...selectedSpaces.value,
            roomId: null,
        };
    }

    const route = useRoute();

    /**
     * Holds the current value of ViewType and space id.
     * This is needed for retrieving data for each space.
     */
    const currentView = computed<CurrentView>(() => {
        switch (route.name) {
            case 'BookingFloorSelection':
                return {type: BookingViewType.Location, id: selectedLocationId.value};
            case 'BookingRoomSelection':
                return {type: BookingViewType.Floor, id: selectedFloorId.value};
            case 'BookingMap':
                return {type: BookingViewType.Room, id: selectedRoomId.value};
            default:
                return {type: BookingViewType.All};
        }
    });

    const currentParentRoute = computed(() => {
        switch (currentView.value.type) {
            case BookingViewType.Room:
                return {name: 'BookingRoomSelection', params: {floorId: selectedFloorId.value}};
            case BookingViewType.Floor:
                return {
                    name: 'BookingFloorSelection',
                    params: {locationId: selectedLocationId.value},
                };
            case BookingViewType.Location:
                return {name: 'BookingLocationSelection'};
            default:
                return null;
        }
    });

    const currentSpacePath = computed(() => {
        let path = '';

        if (!selectedLocation.value) {
            return path;
        }

        path += selectedLocation.value.name;

        if (!selectedFloor.value) {
            return path;
        }

        path += ` / ${selectedFloor.value.name}`;

        if (!selectedRoom.value) {
            return path;
        }

        return `${path} / ${selectedRoom.value.name}`;
    });

    return {
        selectedLocationId,
        selectedFloorId,
        selectedRoomId,
        spaceStructure,
        selectLocation,
        selectedLocation,
        selectFloor,
        selectedFloor,
        selectRoom,
        selectedRoom,
        deselectLocation,
        deselectFloor,
        deselectRoom,
        currentView,
        currentSpacePath,
        spaceStructureIsLoading,
        singleSelectionDefaults,
        roomsWithParentData,
        currentParentRoute,
    };
});
